import {Client} from "typesense";

/**
 * Configuration for constructing a properly-configured Typesense Cloud client.
 */
export interface TypesenseCloudClientConfig {
    /** Load-balanced hostname, e.g. "xxx.a1.typesense.net" */
    host: string;
    port: number;
    protocol: string;
    apiKey: string;
    /**
     * Initial per-request timeout in seconds for the first attempt.
     * Subsequent retries use escalating timeouts (doubles each retry,
     * capped at `maxTimeoutSeconds`). Defaults to 5.
     */
    initialTimeoutSeconds?: number;
    /**
     * Maximum per-request timeout in seconds after escalation.
     * Defaults to 30.
     */
    maxTimeoutSeconds?: number;
    /** Seconds to cache search results client-side. Passed through to the Typesense Client. */
    cacheSearchResultsForSeconds?: number;
}

/**
 * Typesense Cloud hostname suffix that indicates a load-balanced cluster
 * endpoint. Individual nodes are derived by appending "-1", "-2", "-3"
 * before the first dot.
 */
const TYPESENSE_CLOUD_SUFFIX = ".a1.typesense.net";

/** Default number of nodes in a Typesense Cloud HA cluster. */
const TYPESENSE_CLOUD_NODE_COUNT = 3;

/**
 * Install escalating timeout and retry-interval behavior on a Typesense
 * Client. Counters reset after `maxTimeoutSeconds * 3` idle gap.
 */
export function installEscalatingTimeouts(
    client: Client,
    initialTimeoutSeconds: number,
    maxTimeoutSeconds: number,
): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiCall = (client as any).apiCall;
    if (!apiCall) return;

    let attempt = 0;
    let retryAttempt = 0;
    let lastActivityMs = 0;

    const newRequestGapMs = maxTimeoutSeconds * 3 * 1000;
    const initialRetryInterval = 0.2;
    const maxRetryInterval = 5;

    function resetIfIdle(now: number): void {
        if (now - lastActivityMs > newRequestGapMs) {
            attempt = 0;
            retryAttempt = 0;
        }
        lastActivityMs = now;
    }

    Object.defineProperty(apiCall, "connectionTimeoutSeconds", {
        get(): number {
            resetIfIdle(Date.now());
            const timeout = Math.min(initialTimeoutSeconds * Math.pow(2, attempt), maxTimeoutSeconds);
            attempt++;
            return timeout;
        },
        configurable: true,
    });

    Object.defineProperty(apiCall, "retryIntervalSeconds", {
        get(): number {
            resetIfIdle(Date.now());
            const interval = Math.min(initialRetryInterval * Math.pow(2, retryAttempt), maxRetryInterval);
            retryAttempt++;
            return interval;
        },
        set() {
            // No-op: absorb post-construction writes from Typesense internals.
        },
        configurable: true,
    });
}

/**
 * Creates a Typesense client with proper multi-node failover, escalating
 * timeouts, and exponential retry backoff for Typesense Cloud clusters.
 *
 * For Typesense Cloud hosts (*.a1.typesense.net), the load-balanced endpoint
 * is set as `nearestNode` and three individual node hostnames are derived as
 * fallbacks (xxx-1, xxx-2, xxx-3). Requests are sent to the nearest node
 * first; on timeout or failure the client rotates through the fallback nodes.
 *
 * Timeout escalation (per-attempt):
 *   - 1st attempt: initialTimeoutSeconds (default 5 s)
 *   - 2nd attempt: 10 s
 *   - 3rd attempt: 20 s
 *   - 4th attempt: capped at maxTimeoutSeconds (default 30 s)
 *
 * Retry-interval backoff (between attempts):
 *   - 1st retry: 0.2 s
 *   - 2nd retry: 0.4 s
 *   - 3rd retry: 0.8 s
 *   - Subsequent: doubles, capped at 5 s
 *
 * Each retry targets a different node in the cluster.
 *
 * For non-cloud hosts (e.g. localhost), a single-node configuration is used
 * with a fixed 5 s timeout (no escalation needed for local dev).
 */
export function createTypesenseClient(config: TypesenseCloudClientConfig): Client {
    const {host, port, protocol, apiKey, initialTimeoutSeconds = 5, maxTimeoutSeconds = 30} = config;

    const isCloudHost = host.endsWith(TYPESENSE_CLOUD_SUFFIX);

    if (isCloudHost) {
        const prefix = host.slice(0, host.indexOf("."));
        const suffix = host.slice(host.indexOf("."));

        const nodes = Array.from({length: TYPESENSE_CLOUD_NODE_COUNT}, (_, i) => ({
            host: `${prefix}-${i + 1}${suffix}`,
            port,
            protocol,
        }));

        const client = new Client({
            nearestNode: {host, port, protocol},
            nodes,
            apiKey,
            connectionTimeoutSeconds: initialTimeoutSeconds,
            retryIntervalSeconds: 0.1,
            ...(config.cacheSearchResultsForSeconds != null && {
                cacheSearchResultsForSeconds: config.cacheSearchResultsForSeconds,
            }),
        });

        installEscalatingTimeouts(client, initialTimeoutSeconds, maxTimeoutSeconds);
        return client;
    }

    return new Client({
        nodes: [{host, port, protocol}],
        apiKey,
        connectionTimeoutSeconds: 5,
        retryIntervalSeconds: 0.1,
        ...(config.cacheSearchResultsForSeconds != null && {
            cacheSearchResultsForSeconds: config.cacheSearchResultsForSeconds,
        }),
    });
}
